"use client";

import type { ReactNode, Ref } from "react";

import { BELOW_STICKY_CHROME_SCROLL_MARGIN } from "@/lib/sticky-chrome-offsets";

/**
 * Rule 58 layout reserve for a free tool's engine answer.
 *
 * The engine answers seconds after submit — outside CLS's 500 ms
 * input-exclusion window — and the answer is far taller than the form, so
 * inserting it on arrival (or scrolling to it then) pushed the guide below it
 * in the scored viewport: CrUX phone p75 CLS 0.80 on /prashna-kundali/, lab
 * 0.20-0.27 on /hi/rashi-calculator/ and 0.46-0.90 on /hi/dasamsa/.
 *
 * The reserve mounts the moment the seeker submits — inside the window, so the
 * one-time push of the content below is excluded — floors the region at a full
 * viewport, and the tool anchors it to the top of the viewport at that same
 * instant (`scrollIntoView({ behavior: "auto", block: "start" })` in a layout
 * effect keyed on the submit, never on the result). The answer then renders
 * INSIDE the reserve: growth past the floor happens below the fold, and a short
 * answer or an engine error never shrinks it.
 *
 * The shared scroll margin clears the sticky chrome (the 100 px header, and
 * from sm the panchang strip under it), so the first line of the reserve (the
 * status, the answer heading, or the error and its recovery button) is not
 * hidden under it.
 */
const RESULT_RESERVE_CLASS = `min-h-screen ${BELOW_STICKY_CHROME_SCROLL_MARGIN}`;

export function ResultReserve({
  ref,
  surface,
  children,
}: {
  ref: Ref<HTMLDivElement>;
  /** Names the tool for tests and probes: `data-result-reserve="<surface>"`. */
  surface: string;
  children: ReactNode;
}) {
  return (
    <div ref={ref} data-result-reserve={surface} className={RESULT_RESERVE_CLASS}>
      {children}
    </div>
  );
}

export function ResultReserveStatus({ label }: { label: string }) {
  return (
    <p
      role="status"
      aria-live="polite"
      className="mt-8 flex items-center gap-3 text-body-small text-text-secondary"
    >
      <span
        aria-hidden
        className="inline-block h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
      />
      {label}
    </p>
  );
}

/**
 * The seeker is looking at the reserve when an engine error arrives (the form
 * scrolled away at submit), so the message is repeated here as plain text — the
 * form's own `role="alert"` already announced it — with a way back to the
 * form. The reserve itself stays mounted so the guide below never jumps up.
 */
export function ResultReserveError({
  message,
  editLabel,
  onEdit,
}: {
  message: string;
  editLabel: string;
  onEdit: () => void;
}) {
  return (
    <div className="mt-8 rounded-lg border border-border-default bg-surface-raised p-4 text-body-small text-text-secondary">
      <p>{message}</p>
      <button
        type="button"
        onClick={onEdit}
        className="mt-3 inline-flex min-h-11 items-center font-semibold text-intent-brand underline underline-offset-4"
      >
        {editLabel}
      </button>
    </div>
  );
}

/** Bring a form back into view and focus its first field (reserve "edit" action). */
export function returnToForm(form: HTMLElement | null): void {
  if (!form) return;
  form.scrollIntoView({ behavior: "auto", block: "center" });
  form.querySelector<HTMLElement>("input:not([type=hidden]), select, textarea")?.focus();
}
